/*  
<summary>
  Functional Component: TreatmentList
  Developer: Sahil Bharti
  Created Date: 06-Jan-2025
  Description: Renders a Treatment management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the Treatment Management interface</returns>
*/

import { faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "@mui/material/Popover";
import { RichTreeView } from "@mui/x-tree-view";
import { CellContext } from "@tanstack/react-table";
import _ from "lodash";
import { observer } from "mobx-react";
import React, {
  ChangeEvent,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import { ColumnType } from "../../../constants/enums/grid-column-type-enum";
import userSettingEnum from "../../../constants/enums/user-setting-enum";
import { useStore } from "../../../contexts/store-provider";
import { treatmentInitialFilterState } from "../../../core/initialState/initial-filter-state";
import IPageInfo, { IPageLimitInfo } from "../../../models/ICommon";
import { ISorting } from "../../../models/ISorting";
import LoaderButton from "../../../shared-components/button/loader-button";
import Grid, { getColumns } from "../../../shared-components/Grid/Grid";
import Pagination from "../../../shared-components/Grid/Pagination";
import DropdownInput from "../../../shared-components/input/DropdownInput";
import AddPopupHOC from "../../../shared-components/popup/add/add-popup-hoc";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import AddEditTreatment from "./components/add-edit-treatment";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import {
  getBusinessTypeIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../helpers/localstorage-helper";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { ActionType } from "../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../constants/enums/module-type-enum";
import { isOperationPermittedRoleBase } from "../../../helpers/action-permission-helper";
import { formatNumberWithCommas } from "../../../constants/constants";

const TreatmentList: React.FC = (props: any) => {
  const {
    treatmentManagementStore,
    preferencesStore,
    masterManagementStore,
    categoryManagementStore,
  } = useStore();
  const {
    GetCategoryManagementListFilterService,
    allCategoryManagement,
    resetCategoryList,
    categoryList,
    getAllChildIds
  } = categoryManagementStore;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedItems, setSelectedItems] = useState("");
  const [itemDetail, setItemDetail] = useState(
    formatMessage("choose_category")
  );
  const [openPanel, setOpenPanel] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const {
    inProgress,
    error,
    GetTreatmentManagementListService,
    GetSearchTreatmentRegistrationFilterService,
    allTreatmentManagement,
    treatmentList,
    resetAddUpdateTreatmentManagementState,
    addUpdateTreatmentManagementState,
    deleteTreatmentManagementState,
    DeleteTreatmentManagementService,
    resetDeleteTreatmentManagement,
    getTreatmentFilter,
    setTreatmentFilterDetail,
    resettreatmentFilterDetail,
  } = treatmentManagementStore;
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const { GetAllBusinessTypeService, getAllBusinessTypes } =
    masterManagementStore;
  const [businessTypeIdSearch, setBusinessTypeIdSearch] = useState(
    treatmentInitialFilterState.BusinessTypeId ?? -1
  );

  const [treatmentNameSearch, setTreatmentNameSearch] = useState(
    treatmentInitialFilterState.TreatmentName ?? ""
  );
  const [showInvalidStores, setShowInvalidStores] = useState(
    treatmentInitialFilterState.IsInvalid ?? false
  );

  const [categoryIdSearch, setCategoryIdSearch] = useState(
    treatmentInitialFilterState.CategoryId ?? -1
  );
  const [freeWordSearch, setFreeWordSearch] = useState("");
  const [searchInput, setSearchInput] = useState(freeWordSearch);

  const userType = getUserTypeFromLocalStorage();
  const businessTypeId = getBusinessTypeIdFromLocalStorage();

  const isFilterActive =
    !!freeWordSearch.trim() ||
    showInvalidStores ||
    categoryIdSearch !== -1 ||
    (userType !== userTypeEnum[userTypeEnum.HeadQuarter] &&
      businessTypeIdSearch !== -1) ||
    (userType === userTypeEnum[userTypeEnum.HeadQuarter] &&
      businessTypeId === -1);

  // function to handle our data based on buisness typeid
  const handleStoreBusinessTypeIdSearch: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event: ChangeEvent<HTMLSelectElement>) => {
      const selectedBusinessType = Number(event.target.value);
      setBusinessTypeIdSearch(selectedBusinessType);
      resetCategoryList();
      setSelectedItems("");
      setSearchInput("");
      setSelectedCategory(null);
      setItemDetail(formatMessage("choose_category"));
    }, []);

  // function to show invalid treatments
  const handleInvalidTreatmentFilter = useCallback(() => {
    // setSelectedItems("");
    setSearchInput("");
    // setSelectedCategory(null);
    // setItemDetail(formatMessage("choose_category"));
    // if (userType !== userTypeEnum[userTypeEnum.HeadQuarter])
    //   setBusinessTypeIdSearch(-1);
    setShowInvalidStores((prev) => !prev);
  }, []);

  const handleSearchClick = () => {
    const searchQuery = searchInput.trim();

    setSelectedItems("");
    setSelectedCategory(null);
    setItemDetail(formatMessage("choose_category"));
    setShowInvalidStores(false);

    if (searchQuery.length === 0) {
      setFreeWordSearch("");
      setSearchInput("");
      setTreatmentNameSearch("");
      callGetTreatmentManagementListService();
      return;
    }
    setFreeWordSearch(searchQuery);
    setSearchInput(searchQuery);

    const numericUserType = Number(userType);
    const validBusinessTypeId =
      numericUserType === userTypeEnum.HeadQuarter
        ? businessTypeId > 0
          ? businessTypeId
          : undefined
        : businessTypeIdSearch > 0
        ? businessTypeIdSearch
        : undefined;

    GetSearchTreatmentRegistrationFilterService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      searchQuery,
      validBusinessTypeId
    );
  };

  useEffect(() => {
    if (searchInput.trim() === "") {
      setSelectedItems("");
      setSelectedCategory(null);
      setItemDetail(formatMessage("choose_category"));
      setShowInvalidStores(false);
      setFreeWordSearch("");
      setTreatmentNameSearch("");
      setCategoryIdSearch(-1);
      callGetTreatmentManagementListService();
    }
  }, [searchInput]);

  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const [sorting, setSorting] = useState<ISorting>({
    column: "treatment_name",
    orderBy: "asc",
  });

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    let pages: number;
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pageInfo.totalPages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  // function to handle sorting on grid
  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */

  const callGetTreatmentManagementListService = useCallback(() => {
    const currentCategoryId = selectedItems
      ? Number(selectedItems)
      : categoryIdSearch;
    if (
      userType === userTypeEnum[userTypeEnum.HeadQuarter] &&
      businessTypeId !== businessTypeIdSearch
    ) {
      setBusinessTypeIdSearch(businessTypeId);
      return;
    }
    let allChildIds = getAllChildIds(categoryList?.CategoryVM || [], currentCategoryId !== -1 ? currentCategoryId : -1)
    const filters = {
      TreatmentName: treatmentNameSearch,
      BusinessTypeId: businessTypeIdSearch,
      CategoryId: currentCategoryId !== -1 ? currentCategoryId : -1,
      IsInvalid: showInvalidStores,
    };

    GetTreatmentManagementListService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      sorting.orderBy,
      sorting.column,
      filters,
      allChildIds
    );
  }, [
    userType,
    businessTypeId,
    businessTypeIdSearch,
    treatmentNameSearch,
    categoryIdSearch,
    showInvalidStores,
    pageInfo.currentPage,
    pageLimitInfo.pageLimit,
    sorting.orderBy,
    sorting.column,
  ]);

  useEffect(() => {
    if (businessTypeIdSearch > -1) {
      GetCategoryManagementListFilterService(businessTypeIdSearch, 1);
    }
    resetCategoryList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTypeIdSearch]);

  useEffect(() => {
    GetAllBusinessTypeService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    callGetTreatmentManagementListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetTreatmentManagementListService]);

  useEffect(() => {
    setTreatmentFilterDetail({
      ...getTreatmentFilter,
      IsInvalid: showInvalidStores,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInvalidStores]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetTreatmentManagementListService();
      updateLanguageChangedFlag();
      setItemDetail(formatMessage("choose_category"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: treatmentList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTreatmentManagement]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageInfo.currentPage" of the useEffect changed.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetTreatmentManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateTreatmentManagementState.success) {
      resetAddUpdateTreatmentManagementState();
      callGetTreatmentManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateTreatmentManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteTreatmentManagementState.success) {
      resetDeleteTreatmentManagement();
      if (allTreatmentManagement?.length == 1) {
        if (pageInfo.currentPage > 1)
          setPageInfo({
            ...pageInfo,
            currentPage: 1,
            isRefreshPage: true,
            isPagerChange: true,
          });
        else callGetTreatmentManagementListService();
      } else callGetTreatmentManagementListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTreatmentManagementState.success]);

  /**
   * This function shows the add user pop up from where we can add new user and update existing user. we use id to identify
   * that we are adding or updating user.
   * "Id == -1" : we are adding new user
   * "Id != -1" : means we are updating existing user
   */
  const openAddTreatmentManagementHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.addToggleHandler();
    // if (userType !== userTypeEnum[userTypeEnum.HeadQuarter])
    //   setBusinessTypeIdSearch(-1);
    // setFreeWordSearch("");
    // resetCategoryList();
    // setSelectedItems("");
    // setSelectedCategory(null);
    // setItemDetail(formatMessage("choose_category"));
    // setShowInvalidStores(false);
    // setCategoryIdSearch(-1);
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetTreatmentManagementListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  /**
   * This function deletes the user by providing Id to the Api
   */
  const deleteHandler = () => {
    DeleteTreatmentManagementService(selectedId);
  };

  const findObjByKeyWithPath = (
    data: any[],
    key: number
  ): { obj: any; path: string } | null => {
    const search = (
      data: any[],
      key: number,
      path: string[]
    ): { obj: any; path: string } | null => {
      for (const obj of data) {
        const currentPath = [...path, obj.title];

        if (obj.key === key) {
          return { obj, path: currentPath.join(" > ") };
        }

        if (obj.children && obj.children.length > 0) {
          const found = search(obj.children, key, currentPath);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    // Start the search with an empty path
    return search(data, key, []);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);

  const columns = getColumns<any, number>(
    [
      {
        columnType: ColumnType.Hidden,
        accessorKey: "Id",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <>
              <p>{row.TreatmentName}</p>
              <small className="text-muted">{row.BusinessType}</small>
            </>
          );
        },
        accessorKey: "TreatmentName",
        displayName: "treatment_name",
        sortingName: "treatment_name",
      },

      {
         columnType: ColumnType.Sorting,
        accessorKey: "RegistrationType",
        displayName: "registration_type",
        sortingName: "registration_type",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <>
              <p><i className="bi bi-currency-yen me-1"></i> {formatNumberWithCommas(row.FeeIncludingTax)}</p>
            </>
          );
        },
        accessorKey: "FeeIncludingTax",
        displayName: "fee",
        sortingName: "fee_including_tax",
      },
      {
        columnType: ColumnType.Sorting,
        accessorKey: "Category",
        displayName: "category",
        sortingName: "category",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                openAddTreatmentManagementHandler(row);
              }}
            >
              <i className="bi bi-eye me-2"></i>
              <FormattedMessage id="see_details" />
            </Button>
          );
        },
        accessorKey: "isEdit",
        displayName: "",
      },
    ],
    sorting,
    handleSortingChange
  );

  return (
    <React.Fragment>
      {
        (inProgress ||
          addUpdateTreatmentManagementState.inProgress ||
          deleteTreatmentManagementState.inProgress) &&
          <FormLoader
            loading={
              inProgress ||
              addUpdateTreatmentManagementState.inProgress ||
              deleteTreatmentManagementState.inProgress
            }
          />
      }

      {props.showAddPopup && (
        <AddEditTreatment
          id={selectedId}
          modalClosed={props.addToggleHandler}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_User"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_user_confirm"
          isLoading={deleteTreatmentManagementState.inProgress}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="treatment_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="master_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="treatment_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row g-2 align-items-center mb-3">
          <div className="col-xl-8 col-lg-7 col-12 search_box">
            <div className="row g-2">
              <div className="col-xl-4 col-sm-6 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleSearchClick}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_free_word")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchClick();
                      }
                    }}
                  />
                </div>
              </div>

              {/* <div className="col-xl-4 col-sm-6 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("treatment_name")}
                    value={treatmentNameSearch}
                    onChange={handleTreatmentNameSearch}
                  />
                </div>
              </div> */}
              {userType !== userTypeEnum[userTypeEnum.HeadQuarter] && (
                <div className="col-xl-4 col-sm-6 col-12">
                  <div className="input-group">
                    <div className="input-group-text">
                      <span
                        className="cursor-pointer"
                        onClick={() => {
                          setTreatmentFilterDetail({
                            ...getTreatmentFilter,
                            BusinessTypeId: businessTypeIdSearch,
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </span>
                    </div>
                    <DropdownInput
                      className="form-control"
                      placeholder={formatMessage("search_by_business_type")}
                      value={businessTypeIdSearch}
                      disabled={
                        userType === userTypeEnum[userTypeEnum.HeadQuarter]
                      }
                      onChange={handleStoreBusinessTypeIdSearch}
                      options={getAllBusinessTypes.map((option) => ({
                        label: option.value,
                        value: option.id,
                      }))}
                    />
                  </div>
                </div>
              )}
              <div className="col-xl-4 col-sm-6 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      // onClick={handleSearchClick}
                    />
                  </div>
                  {/* <input
                    value={itemDetail}
                    placeholder="Select an option"
                    onClick={handleOpen}
                    disabled={!(allCategoryManagement.length > 0)}
                    readOnly
                    className={`form-select form-control ${
                      allCategoryManagement.length > 0
                        ? "category-dropdown"
                        : ""
                    }`}
                  /> */}
                  <button
                    type="button"
                    disabled={!(allCategoryManagement.length > 0)}
                    onClick={handleOpen}
                    className={`form-select form-control ${
                      allCategoryManagement.length > 0
                        ? "category-dropdown"
                        : ""
                    }`}
                  >
                    {itemDetail}
                  </button>
                  <Popover
                    open={isOpen && allCategoryManagement.length > 0}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    disableAutoFocus // Prevent Popover from auto-focusing
                  >
                    <RichTreeView
                      checkboxSelection
                      selectedItems={selectedItems}
                      onSelectedItemsChange={(
                        e: any,
                        newSelectedItems: any
                      ) => {
                        if (newSelectedItems) {
                          setSelectedItems(newSelectedItems);
                          setSearchInput("");
                          setItemDetail(
                            findObjByKeyWithPath(
                              allCategoryManagement,
                              Number(newSelectedItems)
                            )?.path!
                          );
                          setSelectedCategory(
                            findObjByKeyWithPath(
                              allCategoryManagement,
                              Number(newSelectedItems)
                            )?.obj
                          );
                          setCategoryIdSearch(Number(newSelectedItems));
                        } else {
                          setSelectedItems("");
                          setSelectedCategory(null);
                          setCategoryIdSearch(-1);
                          setItemDetail(formatMessage("choose_category"));
                        }
                        handleClose();
                      }}
                      items={allCategoryManagement}
                    />
                  </Popover>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-4 col-lg-5 col-12">
            <div className="d-flex justify-content-end align-items-center">
              <label className="form-check-label">
                <input
                  type="checkbox"
                  className="form-check-input me-2 mt-0"
                  checked={showInvalidStores}
                  onChange={() => handleInvalidTreatmentFilter()}
                />
              
                {formatMessage("show_invalid_treatment")}
              </label>
              {isFilterActive && (
                <button
                  className={`ms-3 btn ${
                    isFilterActive ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => {
                    setTreatmentNameSearch("");
                    setShowInvalidStores(false);
                    setSelectedItems("");
                    setSelectedCategory(null);
                    setItemDetail(formatMessage("choose_category"));
                    setFreeWordSearch("");
                    setSearchInput("");
                    setCategoryIdSearch(-1);

                    if (userType === userTypeEnum.HeadQuarter.toString()) {
                      setBusinessTypeIdSearch(businessTypeId);
                    } else {
                      setBusinessTypeIdSearch(-1);
                    }
                  }}
                  title={formatMessage("clear_filter")}
                >
                  <svg
                    id="fi_3126668"
                    enableBackground="new 0 0 512 512"
                    height="18"
                    viewBox="0 0 512 512"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#ffffff"
                  >
                    <g>
                      <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                      <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                    </g>
                  </svg>
                </button>
              )}

              {isOperationPermittedRoleBase(
                ModuleType.TreatmentManagement,
                ActionType.Add
              ) && (
                <button
                  className="btn btn-primary ms-3"
                  onClick={() => {
                    openAddTreatmentManagementHandler({ Id: -1 });
                  }}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  {formatMessage("add_treatment")}
                </button>
              )}
            </div>
          </div>
        </div>
        {allTreatmentManagement.length > 0 ? (
          <div className={`${openPanel ? "panelOpen" : ""}`}>
            <div>
              <div className="card changingPanel">
                <div className="tableDesign row-eq-height tableDevices">
                  <div className="table-responsive" data-testid="table">
                    {allTreatmentManagement && (
                      <Grid
                        data={allTreatmentManagement}
                        columns={columns}
                        isInProgress={false}
                        hiddenColumns={["Id"]}
                      />
                    )}
                  </div>
                </div>
              </div>
              {allTreatmentManagement.length > 0 ? (
                <div className="d-flex align-items-center justify-content-center p-2 tableFooter">
                  <Pagination
                    isRefreshPage={pageInfo.isRefreshPage}
                    totalPages={pageInfo.totalPages}
                    pageLimit={pageLimitInfo.pageLimit}
                    onPageChanged={onPageChanged}
                    disablePagination={disablePagination}
                    setIsRefreshPage={setIsRefreshPage}
                  />

                  <select
                    data-testid="pageSelect"
                    className="pageLimit ms-1"
                    onChange={onPageLimitChanged}
                    value={pageLimitInfo.pageLimit}
                  >
                    {pageLimitOptions.map((op) => (
                      <option key={op} value={op}>
                        {op} {formatMessage("rows")}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        ) : (
          <div className="message text-center">
            <img src={noMessageIcon} alt="" className="no_message" />
            {freeWordSearch.trim().length > 0 || showInvalidStores ? (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_data_available" />
                </p>
              </>
            ) : (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_treatment" />
                </p>
                <p className="mb-3">{formatMessage("no_treatment_message")}</p>
                {isOperationPermittedRoleBase(
                  ModuleType.TreatmentManagement,
                  ActionType.Add
                ) && (
                  <LoaderButton
                    text="add_treatment"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      openAddTreatmentManagementHandler({ Id: -1 })
                    }
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddPopupHOC(
  ShowPopupHOC(DeletePopupHOC(observer(TreatmentList)))
);
